import { useState } from "react";
import { Check, Cylinder, KeyRound, Lock } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export default function ResetPassword({ token, onDone }) {
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 6) {
      setError("Password must be at least 6 characters");
      return;
    }
    if (password !== confirm) {
      setError("Passwords do not match");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Reset failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0B0F17] bg-grid flex items-center justify-center px-4" data-testid="reset-password-page">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-3 justify-center mb-8">
          <div className="h-12 w-12 rounded-lg bg-amber-500/10 border border-amber-500/40 flex items-center justify-center">
            <Cylinder className="h-7 w-7 text-amber-400" />
          </div>
          <h1 className="text-2xl font-extrabold tracking-tight font-mono text-slate-100">TANK VOLUME CALCULATOR</h1>
        </div>

        <div className="bg-[#131924] border border-[#243046] rounded-xl p-8 shadow-2xl rise">
          {done ? (
            <div className="space-y-4 text-center" data-testid="reset-success-message">
              <div className="mx-auto h-12 w-12 rounded-full bg-emerald-500/10 border border-emerald-500/40 flex items-center justify-center">
                <Check className="h-6 w-6 text-emerald-400" />
              </div>
              <h2 className="text-lg font-semibold font-mono text-slate-100">Password updated</h2>
              <p className="text-sm text-slate-400 font-mono">You can now sign in with your new password.</p>
              <Button data-testid="reset-go-login-btn" onClick={onDone} className="bg-amber-500 text-[#0B0F17] hover:bg-amber-400 font-semibold">Go to sign in</Button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-5" data-testid="reset-form">
              <div>
                <h2 className="text-lg font-semibold font-mono text-slate-100 flex items-center gap-2"><KeyRound className="h-5 w-5 text-amber-400" />Set a new password</h2>
                <p className="text-xs text-slate-500 font-mono mt-1">Choose a new password for your account.</p>
              </div>
              <div>
                <p className="text-[11px] font-mono text-slate-500 mb-1">New password</p>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                  <Input data-testid="reset-password-input" type="password" required autoFocus value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" className="pl-9 bg-[#0D131E] border-[#243046] font-mono text-slate-100 focus-visible:ring-amber-500" />
                </div>
              </div>
              <div>
                <p className="text-[11px] font-mono text-slate-500 mb-1">Confirm password</p>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                  <Input data-testid="reset-confirm-input" type="password" required value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="••••••••" className="pl-9 bg-[#0D131E] border-[#243046] font-mono text-slate-100 focus-visible:ring-amber-500" />
                </div>
              </div>
              {error && <p data-testid="reset-error" className="text-sm text-red-400 font-mono bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2">{error}</p>}
              <Button data-testid="reset-submit-btn" type="submit" disabled={busy} className="w-full bg-amber-500 text-[#0B0F17] hover:bg-amber-400 font-semibold">
                {busy ? "Updating…" : "Update password"}
              </Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
